import { useCallback, useEffect, useState } from "react";
import {
    Alert, Badge, Button, Card, Col, Form, Input, Radio, Row, Select, Space, Table, Tag, Tooltip, Typography, message,
} from "antd";
import { ReloadOutlined, SendOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import ApiService from "../../core/service/api-service";
import AppConfig from "../../core/service/AppConfig";
import CardTitle from "../../core/components/CardTitle";
import LayoutWithSider from "../../core/components/layout/layout-with-sider/LayoutWithSider";
import { dateFormat } from "../../core/utils/date-utils";
import { useSignalR } from "../../core/context/SignalRContext";
import { matchesSearch } from "../../core/utils/text-search";

// Sunucudaki NotificationTargetEnum ile birebir.
const TARGET = { SELECTED: 1, ALL_ENABLED: 2, ONLINE: 3 };
const STATUS = { CONTINUE: 1, COMPLETED: 2, ERROR: 3 };
const PAGE_SIZE = 20;

const TYPE_LABEL = {
    1: 'Report Excel',
    2: 'Study Share',
    3: 'Study Zip',
    4: 'KOS',
    5: 'Admin Message',
};

/**
 * Yönetici bildirim paneli: elle bildirim gönderme (test) + canlı bağlantı ve teslim takibi.
 *
 * Bağlı istemci listesi BELLEKTEN gelir ve tek instance varsayar; kopan bağlantılar her zaman
 * haber verilmediği için "en iyi çaba" bir görüntüdür — izleme amaçlıdır, yetki kararı değil.
 */
export default function NotificationAdminPage() {
    const { t } = useTranslation();
    const { onNotification, connected } = useSignalR();

    const [form] = Form.useForm();
    const [target, setTarget] = useState(TARGET.SELECTED);
    const [sending, setSending] = useState(false);

    const [recipients, setRecipients] = useState([]);
    const [online, setOnline] = useState([]);
    const [connections, setConnections] = useState([]);

    const [sent, setSent] = useState([]);
    const [sentTotal, setSentTotal] = useState(0);
    const [sentPage, setSentPage] = useState(1);
    const [loadingSent, setLoadingSent] = useState(false);

    useEffect(() => {
        document.title = `${t('Notification Management')} | ${AppConfig.getRisTitle()}`
    }, []);

    const loadRecipients = useCallback(async () => {
        const res = await ApiService.Notification.GetRecipients();
        if (res.success) setRecipients(res.data || []);
    }, []);

    const loadLive = useCallback(async () => {
        const [onlineRes, connRes] = await Promise.all([
            ApiService.Notification.GetOnlineUsers(),
            ApiService.Notification.GetConnections(),
        ]);

        if (onlineRes.success) setOnline(onlineRes.data || []);
        if (connRes.success) setConnections(connRes.data || []);
    }, []);

    const loadSent = useCallback(async (page) => {
        setLoadingSent(true);
        const res = await ApiService.Notification.GetSentNotifications({
            filter: {},
            paginationQuery: { pageNumber: page, pageSize: PAGE_SIZE },
            orderByItems: [],
        });
        setLoadingSent(false);

        if (res.success) {
            setSent(res.data || []);
            setSentTotal(res.pagination?.totalRecords || 0);
            setSentPage(page);
        }
    }, []);

    const refreshAll = useCallback(async () => {
        await Promise.all([loadRecipients(), loadLive(), loadSent(1)]);
    }, [loadRecipients, loadLive, loadSent]);

    useEffect(() => {
        refreshAll();
    }, []);

    // Yöneticinin kendisine gelen bir bildirim de olsa, canlı durum değişti demektir: takip
    // listesini tazele. (Panel açıkken gönderim yapınca sonucu anında görmek için.)
    useEffect(() => {
        if (!onNotification?.key)
            return;

        loadLive();
        loadSent(sentPage);
    }, [onNotification?.key]);

    const send = async () => {
        const values = await form.validateFields();

        setSending(true);
        const res = await ApiService.Notification.Send({
            target,
            userIdList: target === TARGET.SELECTED ? (values.userIdList || []) : [],
            name: values.name,
            description: values.description,
        });
        setSending(false);

        if (!res.success)
            return;

        const { sent: sentCount, deliveredOnline } = res.data || {};

        if (!sentCount)
            message.warning(t('NOTIFICATION_NO_RECIPIENT'));
        else
            message.success(t('NOTIFICATION_SEND_RESULT', { count: sentCount, online: deliveredOnline || 0 }));

        form.resetFields(['name', 'description']);
        await Promise.all([loadLive(), loadSent(1)]);
    }

    const onlineColumns = [
        {
            title: t('User'),
            dataIndex: 'userName',
            key: 'userName',
            render: (text) => text || '-'
        },
        {
            title: t('Connections'),
            dataIndex: 'connectionCount',
            key: 'connectionCount',
            width: 110,
            render: (text) => <Tag color={'blue'}>{text}</Tag>
        },
        {
            title: t('Connected At'),
            dataIndex: 'connectedAt',
            key: 'connectedAt',
            width: 160,
            render: (text) => (text ? dateFormat(text) : '-')
        },
        { title: 'IP', dataIndex: 'ip', key: 'ip', width: 140, render: (text) => text || '-' },
    ];

    const sentColumns = [
        { title: 'Id', dataIndex: 'id', key: 'id', width: 80 },
        {
            title: t('User'),
            dataIndex: 'userName',
            key: 'userName',
            width: 170,
            render: (text, record) => (
                <Space size={4}>
                    <Badge status={record.isOnline ? 'success' : 'default'} />
                    <span>{text || '-'}</span>
                </Space>
            )
        },
        { title: t('Name'), dataIndex: 'name', key: 'name' },
        {
            title: t('Type'),
            dataIndex: 'type',
            key: 'type',
            width: 130,
            render: (text) => TYPE_LABEL[text] || text
        },
        {
            title: t('Status'),
            dataIndex: 'status',
            key: 'status',
            width: 120,
            render: (text) => {
                if (text === STATUS.CONTINUE) return <Tag color={'processing'}>{t('Continue')}</Tag>
                if (text === STATUS.ERROR) return <Tag color={'error'}>{t('Error')}</Tag>
                return <Tag color={'success'}>{t('Completed')}</Tag>
            }
        },
        {
            title: t('Date'),
            dataIndex: 'createdDate',
            key: 'createdDate',
            width: 160,
            render: (text) => (text ? dateFormat(text) : '-')
        },
        {
            title: t('Read'),
            dataIndex: 'readDate',
            key: 'readDate',
            width: 160,
            render: (text) => (text
                ? <Tooltip title={dateFormat(text)}><Tag color={'green'}>{t('Read')}</Tag></Tooltip>
                : <Tag>{t('Unread')}</Tag>)
        },
    ];

    // Sayfa çerçevesi (sabit başlığın altı, yan/alt boşluklar) LayoutWithSider'dan gelir;
    // yan panel gerekmediği için sidebarComponent verilmez → şerit 0 genişliğe çöker.
    return (
        <LayoutWithSider fullWidth={true}>
        <Card title={<CardTitle title={t('Notification Management')} />}>
        <Row gutter={[12, 12]}>
            <Col xs={24} lg={9}>
                <Card
                    title={t('Send Notification')}
                    extra={connected
                        ? <Tag color={'green'}>{t('Connected')}</Tag>
                        : <Tag color={'orange'}>{t('Offline')}</Tag>}
                >
                    <Alert type={'info'} showIcon message={t('NOTIFICATION_ADMIN_HINT')} style={{ marginBottom: 12 }} />

                    <Form form={form} layout={'vertical'} onFinish={send}>
                        <Form.Item label={t('Recipients')}>
                            <Radio.Group
                                value={target}
                                onChange={(e) => setTarget(e.target.value)}
                                options={[
                                    { label: t('Selected Users'), value: TARGET.SELECTED },
                                    { label: t('Online Users'), value: TARGET.ONLINE },
                                    { label: t('All Users'), value: TARGET.ALL_ENABLED },
                                ]}
                            />
                        </Form.Item>

                        {target === TARGET.SELECTED && (
                            <Form.Item
                                name={'userIdList'}
                                rules={[{ required: true, message: t('NOTIFICATION_PICK_RECIPIENT') }]}
                            >
                                <Select
                                    mode={'multiple'}
                                    allowClear
                                    showSearch
                                    placeholder={t('Select Users')}
                                    filterOption={(input, option) => matchesSearch(option?.label, input)}
                                    options={recipients.map(x => ({
                                        value: x.id,
                                        label: `${x.visibleName || x.username}${x.isOnline ? ' ●' : ''}`,
                                    }))}
                                />
                            </Form.Item>
                        )}

                        <Form.Item
                            label={t('Name')}
                            name={'name'}
                            rules={[{ required: true, message: t('NOTIFICATION_TITLE_REQUIRED') }]}
                        >
                            <Input maxLength={200} />
                        </Form.Item>

                        <Form.Item label={t('Description')} name={'description'}>
                            <Input.TextArea rows={3} maxLength={1000} />
                        </Form.Item>

                        <Button type={'primary'} icon={<SendOutlined />} loading={sending} htmlType={'submit'}>
                            {t('Send')}
                        </Button>
                    </Form>
                </Card>
            </Col>

            <Col xs={24} lg={15}>
                <Card
                    title={(
                        <Space>
                            {t('Online Users')}
                            <Tag color={'blue'}>{online.length}</Tag>
                            <Typography.Text type={'secondary'} style={{ fontSize: 12 }}>
                                {t('Connections')}: {connections.length}
                            </Typography.Text>
                        </Space>
                    )}
                    extra={<Button size={'small'} icon={<ReloadOutlined />} onClick={loadLive} />}
                    style={{ marginBottom: 12 }}
                >
                    <Table
                        size={'small'}
                        rowKey={'userId'}
                        dataSource={online}
                        columns={onlineColumns}
                        pagination={false}
                        scroll={{ y: 220 }}
                        locale={{ emptyText: t('NOTIFICATION_NO_ONLINE') }}
                    />
                </Card>

                <Card
                    title={t('Sent Notifications')}
                    extra={<Button size={'small'} icon={<ReloadOutlined />} onClick={() => loadSent(sentPage)} />}
                >
                    <Table
                        size={'small'}
                        rowKey={'id'}
                        loading={loadingSent}
                        dataSource={sent}
                        columns={sentColumns}
                        scroll={{ x: true }}
                        pagination={{
                            current: sentPage,
                            pageSize: PAGE_SIZE,
                            total: sentTotal,
                            showSizeChanger: false,
                            onChange: (page) => loadSent(page),
                        }}
                    />
                </Card>
            </Col>
        </Row>
        </Card>
        </LayoutWithSider>
    )
}
